import { END_REASON_VALUES } from "@/constants/categorical-metric-values"

import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { CategoricalMetricRecord } from "../base/CategoricalMetricRecord"

export class EndReasonMetricRecord extends CategoricalMetricRecord {
  documentation = "Captures the reason for why the call ended."
  overviewSize = "small" as const

  getCategoricalOptions(): string[] {
    return [...END_REASON_VALUES]
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }

    const validOutputs = metricOutputs.filter((mo) => mo.string_value !== null)

    if (validOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">No metric data available.</div>
      )
    }

    return (
      <AggregateOverviewString
        mos={validOutputs}
        onBarClick={context?.onBarClick}
        activeFilters={context?.activeFilters}
      />
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const rawEndReason = mo.string_value
    const endReason = typeof rawEndReason === "string" ? rawEndReason : ""
    const explanation = endReason
      ? `The call ended with reason: ${formatMetricDisplayName(endReason)}.`
      : ""

    return (
      <div className="space-y-2">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="text-sm text-amber-600 font-medium mb-1">
            End Reason
          </div>
          <div className="text-2xl font-bold text-orange-800">
            {formatMetricDisplayName(endReason)}
          </div>
          {explanation && (
            <div className="text-sm text-amber-700 mt-2">{explanation}</div>
          )}
        </div>
      </div>
    )
  }

  renderMetricType(): React.ReactNode {
    return "Built-in"
  }
}
